import InlineAppCTA from "@/components/InlineAppCTA";
import AIAstrologerCTA from "@/components/AIAstrologerCTA";
import CalculatorCrossLinks from "@/components/CalculatorCrossLinks";
import JsonLd from "@/components/JsonLd";
import Term from "@/components/Term";
import { faqPageSchema, type FAQItem } from "@/lib/schema";

const DASHA_FAQS: FAQItem[] = [
  {
    question: "What is a Mahadasha in Vedic astrology?",
    answer:
      "A Mahadasha is a major planetary period in the Vimshottari Dasha system. Each of the nine Vedic planets rules a specific number of years: Ketu (7), Venus (20), Sun (6), Moon (10), Mars (7), Rahu (18), Jupiter (16), Saturn (19), and Mercury (17). During a Mahadasha, the ruling planet's significations dominate your life experiences, career direction, relationships, and spiritual growth.",
  },
  {
    question: "How do I know which Mahadasha I am currently in?",
    answer:
      "Enter your birth details above to calculate your Mahadasha sequence. The first Mahadasha is determined by your Moon's nakshatra at birth. From the balance of the first period, subsequent Mahadashas follow the fixed sequence: Ketu, Venus, Sun, Moon, Mars, Rahu, Jupiter, Saturn, Mercury.",
  },
  {
    question: "What is the difference between Mahadasha and Antardasha?",
    answer:
      "Mahadasha is the major period, while Antardasha is a sub-period within each Mahadasha. Each Mahadasha contains nine Antardashas. The Mahadasha sets the overall theme, and the Antardasha modifies it.",
  },
  {
    question: "Can Dasha periods predict specific events?",
    answer:
      "Vedic astrologers use the Dasha system to identify the timing of major life events such as career changes, marriage, childbirth, and financial shifts. By analysing which Mahadasha, Antardasha, and Pratyantardasha are active, predictions can be narrowed to specific months or even weeks.",
  },
  {
    question: "Is birth time required for Dasha calculation?",
    answer:
      "Birth time is optional but recommended. The Moon moves through one nakshatra in approximately one day, so even without exact birth time the nakshatra can usually be determined. However, exact birth time is needed to calculate the precise Dasha balance.",
  },
];

export function DashaSeoContent() {
  return (
    <section
      className="mx-auto max-w-2xl px-4 sm:px-6 py-12 sm:py-16"
      style={{ color: "var(--ish-text-muted)" }}
    >
      <JsonLd data={faqPageSchema(DASHA_FAQS)} />
      <InlineAppCTA />
      <div className="divider-warm mb-10" />

      <h2
        className="text-xl font-bold mb-4"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        What is Vimshottari Dasha?
      </h2>
      <p className="text-[14px] leading-relaxed mb-4">
        Vimshottari <Term k="dasha">Dasha</Term> is the most widely used planetary period system in
        Vedic astrology (Jyotish). The word &ldquo;Vimshottari&rdquo; means &ldquo;120&rdquo; in
        Sanskrit, referring to the total cycle of 120 years across nine planetary periods. Each
        period, called a <Term k="mahadasha">Mahadasha</Term>, is governed by one of the nine Vedic
        planets (Navagraha) and colours all events, decisions, and experiences during that time.
      </p>
      <p className="text-[14px] leading-relaxed mb-6">
        Your Mahadasha sequence is determined by the <Term k="nakshatra">nakshatra</Term> (lunar
        mansion) occupied by the Moon at the exact moment of your birth. Since there are 27
        nakshatras and each is ruled by one of the nine planets, the starting Mahadasha lord &mdash;
        and the remaining balance of that first period &mdash; is unique to each birth chart. This
        is why two people born on the same day but at different times can experience vastly
        different life trajectories.
      </p>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Mahadasha and Antardasha Explained
      </h2>
      <p className="text-[14px] leading-relaxed mb-4">
        <strong style={{ color: "var(--ish-dark)" }}>Mahadasha (Major Period):</strong> The primary
        planetary period that sets the overarching theme. Each Mahadasha lasts a fixed number of
        years: Ketu (7), Venus (20), Sun (6), Moon (10), Mars (7), Rahu (18), Jupiter (16), Saturn
        (19), Mercury (17). The total equals 120 years.
      </p>
      <p className="text-[14px] leading-relaxed mb-4">
        <strong style={{ color: "var(--ish-dark)" }}>
          <Term k="antardasha">Antardasha</Term> (Sub-Period):
        </strong>{" "}
        Each Mahadasha is subdivided into nine Antardashas (Bhukti periods), one for each planet in
        the same Vimshottari sequence. The Antardasha modifies the Mahadasha theme &mdash; for
        example, Jupiter Antardasha within Saturn Mahadasha combines discipline with wisdom and
        expansion.
      </p>
      <p className="text-[14px] leading-relaxed mb-6">
        <strong style={{ color: "var(--ish-dark)" }}>Pratyantardasha (Sub-Sub-Period):</strong> For
        even finer timing, each Antardasha is further divided into Pratyantardashas using the same
        proportional system. This fractal structure allows Vedic astrology to predict the timing of
        specific events with remarkable precision, often to within weeks or months.
      </p>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        How the Moon Nakshatra Determines Your Dasha
      </h2>
      <p className="text-[14px] leading-relaxed mb-4">
        The 27 nakshatras are distributed among the nine planets in groups of three. Ashwini, Magha,
        and Moola are ruled by Ketu. Bharani, Purva Phalguni, and Purva Ashadha are ruled by Venus.
        Krittika, Uttara Phalguni, and Uttara Ashadha are ruled by the Sun.
      </p>
      <p className="text-[14px] leading-relaxed mb-6">
        The ruling planet of your Moon&apos;s nakshatra becomes your starting Mahadasha lord. The
        degree of the Moon within the nakshatra determines the Dasha balance — how much of the first
        period remains at birth. Our calculator determines your Moon nakshatra from precise Swiss
        Ephemeris calculations and maps it to the correct starting Mahadasha lord.
      </p>

      <h2
        className="text-xl font-bold mb-4 mt-10"
        style={{ fontFamily: "var(--font-pt-serif)", color: "var(--ish-dark)" }}
      >
        Frequently Asked Questions
      </h2>
      <div className="space-y-4">
        {DASHA_FAQS.map((item) => (
          <details key={item.question} className="group">
            <summary
              className="text-[14px] font-semibold cursor-pointer"
              style={{ color: "var(--ish-dark)" }}
            >
              {item.question}
            </summary>
            <p className="mt-2 text-[13px] leading-relaxed pl-4">{item.answer}</p>
          </details>
        ))}
      </div>

      <AIAstrologerCTA context="kundli" />
      <CalculatorCrossLinks currentPage="dasha-calculator" />
    </section>
  );
}
